/**
 * The hourly forecast on the hangar screen (Kyle, 2026-10-03: "build a forecast in that we can choose to
 * display if we want"): the US National Weather Service's forecast for the site's own position, the next
 * hours as a strip. Each hour: the time, the wind as an arrow and a speed (gusts when NWS gives them), the
 * temperature, the sky in NWS's own short words, and the chance of rain when there is one.
 *
 * It is a forecast and it says so in its header, under the source's name; it is never shown under
 * "Weather observations" (observation-format §7). Only the NWS hourly product is drawn here; an airport
 * forecast (TAF), when a site has one, is kept for a later panel.
 */
import { formatTemperature, formatWind } from '@skyweather/weather-core'
import { localTime, windUnit } from './format.js'
import type { ForecastItem, HourlyPeriod, SiteUnits } from './types.js'
import { WindArrow } from './WindArrow.js'

/** How many hours the strip shows. */
export const FORECAST_HOURS_SHOWN = 10

export function isHourlyPeriod(p: unknown): p is HourlyPeriod {
  return typeof p === 'object' && p !== null && typeof (p as HourlyPeriod).from === 'number' && typeof (p as HourlyPeriod).to === 'number' && 'summary' in (p as object)
}

/** The hours still to come (and the current one), from the newest NWS forecast. */
export function upcomingHours(items: readonly ForecastItem[], now: number, count = FORECAST_HOURS_SHOWN): { item: ForecastItem; hours: HourlyPeriod[] } | null {
  const nws = items.filter((i) => i.kind === 'nws-forecast').sort((a, b) => b.issuedAt - a.issuedAt)[0]
  if (!nws) return null
  const hours = nws.periods.filter(isHourlyPeriod).filter((p) => p.to > now).sort((a, b) => a.from - b.from).slice(0, count)
  return hours.length === 0 ? null : { item: nws, hours }
}

/** NWS's sky words, shortened for a cell: "Chance Showers And Thunderstorms" → "Chance Showers". */
export function shortSky(summary: string): string {
  const words = summary.replace(/\s+then\s+.*$/i, '').split(/\s+/).filter(Boolean)
  return words.slice(0, 2).join(' ')
}

export function ForecastPanel({ items, units, timeZone, now }: { items: ForecastItem[]; units: SiteUnits; timeZone: string; now: number }) {
  const f = upcomingHours(items, now)
  if (!f) return null
  const unit = windUnit(units)
  const temp = units.temp ?? 'C'
  return (
    <section className="fcst" aria-label="Hourly forecast">
      <div className="fcst__head">
        <h2>Forecast · {f.item.label}</h2>
        <span>issued {localTime(f.item.issuedAt, timeZone)}</span>
      </div>
      <ol className="fcst__hours">
        {f.hours.map((h) => (
          <li key={h.from} className={`fcst__hour${h.isDaytime === false ? ' fcst__hour--night' : ''}`}>
            <span className="fcst__time">{localTime(h.from, timeZone)}</span>
            <WindArrow fromDeg={h.windDirDeg} label={h.windDirDeg === null ? 'no direction' : `from ${h.windDirDeg}°`} size={22} />
            <strong className="fcst__wind">
              {h.windKt === null ? '—' : formatWind(h.windKt, unit, { withKnots: false })}
              {h.gustKt !== null && h.windKt !== null && h.gustKt > h.windKt ? <span className="fcst__gust"> G{formatWind(h.gustKt, unit, { withKnots: false, role: 'gust' }).replace(/\s.*$/, '')}</span> : null}
            </strong>
            <span className="fcst__temp">{h.tempC === null ? '' : formatTemperature(h.tempC, temp)}</span>
            <span className="fcst__sky">{shortSky(h.summary)}</span>
            {h.precipPct !== null && h.precipPct > 0 && <span className="fcst__pop">{h.precipPct}%</span>}
          </li>
        ))}
      </ol>
    </section>
  )
}
